/**
 * GlobalAICopilot.tsx - Platform-Wide AI Copilot Sidebar
 *
 * A global AI assistant accessible via Cmd+K from any page.
 * Uses Sheet component for right-side slide-in panel.
 *
 * Features:
 * - Context-aware (shows current page context)
 * - Keyboard shortcut (Cmd+K / Ctrl+K)
 * - Chat interface with message history
 * - Quick suggestion buttons for empty state
 */

import React, { useState, useRef, useEffect, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, User, Send, Loader2, Sparkles, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { useAICopilot, CopilotMessage, PageContextType } from '@/contexts/AICopilotContext';

// Context type display labels — resolved via `globalAICopilot.contextLabels.<type>`
const contextLabelKeys: Record<PageContextType, string> = {
  general: 'globalAICopilot.contextLabels.general',
  workflow: 'globalAICopilot.contextLabels.workflow',
  agent: 'globalAICopilot.contextLabels.agent',
  conversation: 'globalAICopilot.contextLabels.conversation',
  dataset: 'globalAICopilot.contextLabels.dataset',
  function: 'globalAICopilot.contextLabels.function',
  'custom-extensions': 'globalAICopilot.contextLabels.customExtensions',
  app_studio: 'globalAICopilot.contextLabels.appStudio',
};

// Quick suggestions for empty state
const quickSuggestionKeys = [
  'globalAICopilot.suggestions.createWorkflow',
  'globalAICopilot.suggestions.agentConfig',
  'globalAICopilot.suggestions.debugIssue',
  'globalAICopilot.suggestions.availableTools',
];

// Message bubble component
function MessageBubble({ message }: { message: CopilotMessage }) {
  const isUser = message.role === 'user';

  return (
    <div
      className={cn(
        'flex gap-2 mb-3',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      <div
        className={cn(
          'w-8 h-8 rounded-full flex items-center justify-center shrink-0',
          isUser
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted dark:bg-muted'
        )}
      >
        {isUser ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
      </div>

      <div
        className={cn(
          'flex flex-col max-w-[85%]',
          isUser ? 'items-end' : 'items-start'
        )}
      >
        <div
          className={cn(
            'rounded-lg px-3 py-2 text-sm whitespace-pre-wrap',
            isUser
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted dark:bg-muted text-foreground'
          )}
        >
          {message.content}
        </div>
        <span className="text-[10px] text-muted-foreground mt-1">
          {new Date(message.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      </div>
    </div>
  );
}

// Loading indicator
function LoadingIndicator() {
  const { t } = useTranslation('workflows');
  return (
    <div className="flex gap-2 mb-3">
      <div className="w-8 h-8 rounded-full bg-muted dark:bg-muted flex items-center justify-center shrink-0">
        <Bot className="h-4 w-4" />
      </div>
      <div className="flex items-center gap-1 bg-muted dark:bg-muted rounded-lg px-3 py-2">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">{t('globalAICopilot.thinking')}</span>
      </div>
    </div>
  );
}

// Empty state with suggestions
function EmptyState({ onSuggestionClick }: { onSuggestionClick: (text: string) => void }) {
  const { t } = useTranslation('workflows');
  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-4">
      <div className="w-12 h-12 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center mb-4">
        <Sparkles className="h-6 w-6 text-primary" />
      </div>
      <h3 className="text-lg font-medium mb-2">{t('globalAICopilot.title')}</h3>
      <p className="text-sm text-muted-foreground mb-6">
        {t('globalAICopilot.subtitle')}
      </p>
      <div className="flex flex-col gap-2 w-full max-w-xs">
        {quickSuggestionKeys.map((key) => (
          <Button
            key={key}
            variant="outline"
            size="sm"
            className="justify-start text-left h-auto py-2 px-3"
            onClick={() => onSuggestionClick(t(key))}
          >
            <span className="truncate">{t(key)}</span>
          </Button>
        ))}
      </div>
    </div>
  );
}

export function GlobalAICopilot() {
  const { t } = useTranslation('workflows');
  const {
    isOpen,
    close,
    pageContext,
    messages,
    isLoading,
    sendMessage,
    clearMessages,
  } = useAICopilot();

  const [input, setInput] = useState('');
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages, isLoading]);

  // Focus input when sheet opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (input.trim() && !isLoading) {
      sendMessage(input.trim());
      setInput('');
    }
  };

  const handleSuggestionClick = (text: string) => {
    sendMessage(text);
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && close()}>
      <SheetContent
        side="right"
        className="w-[400px] sm:w-[450px] flex flex-col p-0"
      >
        {/* Header */}
        <SheetHeader className="px-4 py-3 border-b dark:border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SheetTitle className="text-base">{t('globalAICopilot.title')}</SheetTitle>
              <Badge variant="secondary" className="text-xs">
                {t(contextLabelKeys[pageContext.type])}
              </Badge>
            </div>
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={clearMessages}
                title={t('globalAICopilot.clearChat')}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
          {pageContext.entityName && (
            <SheetDescription className="text-xs mt-1">
              {pageContext.entityName}
            </SheetDescription>
          )}
        </SheetHeader>

        {/* Messages Area */}
        <ScrollArea ref={scrollAreaRef} className="flex-1 px-4 py-4">
          {messages.length === 0 && !isLoading ? (
            <EmptyState onSuggestionClick={handleSuggestionClick} />
          ) : (
            <>
              {messages.map((message) => (
                <MessageBubble key={message.id} message={message} />
              ))}
              {isLoading && <LoadingIndicator />}
            </>
          )}
        </ScrollArea>

        {/* Input Area */}
        <form
          onSubmit={handleSubmit}
          className="p-4 border-t dark:border-border"
        >
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('globalAICopilot.askPlaceholder')}
              disabled={isLoading}
              className="flex-1"
            />
            <Button
              type="submit"
              size="icon"
              disabled={!input.trim() || isLoading}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-2 text-center">
            {t('globalAICopilot.toggleHintPrefix')} <kbd className="px-1 py-0.5 rounded bg-muted text-[10px]">Cmd+K</kbd> {t('globalAICopilot.toggleHintSuffix')}
          </p>
        </form>
      </SheetContent>
    </Sheet>
  );
}
